/**
 * V3 Case Messaging API — Frontend service extensions for case-centric messaging.
 * All calls are gated by CASE_MESSAGING_V3_ENABLED on the backend.
 */
import api from '../utils/api';

const CaseMessagingAPI = {
  /**
   * Create a V3 thread (requires clinic_id + case_id when V3 is enabled)
   */
  async createThreadV3({ threadType, clinicId, caseId, subject, initialMessage, channel = 'treatx_native' }) {
    // B-2 / M2 — canonical candidate authority: send `context_type=case`
    // only when a `case_id` is actually present. Otherwise the caller
    // is opening a legitimate pre-acceptance conversation (lead /
    // provider-invite / thread-only). Backend V3 validator permits
    // `case_id=None` for `context_type in (lead, offer, null)`.
    const payload = {
      thread_type: threadType,
      clinic_id: clinicId,
      subject,
      initial_message: initialMessage,
      channel,
    };
    if (caseId) {
      payload.case_id = caseId;
      payload.context_type = 'case';
    }
    const response = await api.post('/v2/threads', payload);
    return response.data;
  },

  /**
   * Select an offer for a case
   */
  async selectOffer(leadId, offerId, caseId) {
    const response = await api.post(`/clinic-leads/${leadId}/offers/${offerId}/select`, {
      case_id: caseId,
    });
    return response.data;
  },

  /**
   * Deselect an offer
   */
  async deselectOffer(leadId, offerId) {
    const response = await api.post(`/clinic-leads/${leadId}/offers/${offerId}/deselect`);
    return response.data;
  },

  /**
   * Get cases for a clinic (for case selector)
   */
  async getClinicCases(clinicId, { status, limit = 50 } = {}) {
    const params = new URLSearchParams({ limit });
    if (status) params.append('status', status);
    const response = await api.get(`/cases/clinic/${clinicId}?${params}`);
    return response.data;
  },

  /**
   * Get cases for a patient
   */
  async getPatientCases(patientId) {
    const response = await api.get(`/cases/person/${patientId}`);
    return response.data;
  },

  /**
   * Assign a canonical case role.
   *
   * UAT COORDINATOR-ELIGIBILITY FIX — this client previously called
   * `PUT /cases/{id}/assign-role`, which DOES NOT EXIST on the backend
   * (the canonical route is `PUT /cases/{case_id}/assign`), so no case
   * role assignment ever persisted from the UI. That is why
   * `treatment_cases.assigned_coordinator_id` stayed `SYSTEM_POOL` and
   * the medical recipient resolver could not discover the Coordinator.
   *
   * The backend validates the target server-side (same provider, active
   * canonical membership, required role) before writing the case field
   * and projecting the canonical `participants[]` row.
   */
  async assignCaseRole(caseId, roleField, userId) {
    const response = await api.put(`/cases/${caseId}/assign`, {
      role_field: roleField,
      user_id: userId,
    });
    return response.data;
  },

  /**
   * Assign a sales rep to a case (V3) — delegates to the canonical path.
   */
  async assignCaseSalesRep(caseId, userId) {
    return this.assignCaseRole(caseId, 'assigned_sales_rep_id', userId);
  },
};

export default CaseMessagingAPI;
